import { EntityType } from "@common/Common.types";

// Mirrors the backend RiskHistoryEventKind.
export enum RiskHistoryEventKind {
    Assessed = 1,
    Reassessed = 2,
    Reconfirmed = 3,
    MitigationPromoted = 4,
    ScaleMigrated = 5,
    Retired = 6,
    Reinstated = 7,
    PlanProposed = 8,
    PlanApproved = 9,
    PlanCompleted = 10,
    RiskAccepted = 11,
    AreaConfigChanged = 12,
    RiskAvoided = 13,
    PlanStarted = 14,
    PlanPlanned = 15,
    PlanDeferred = 16,
    PlanReactivated = 17,
    PlanCancelled = 18,
    MatrixRebased = 19,
}

// Translation keys per event kind. Shared by the area history table and the single-assessment timeline dialog.
export const eventKindTranslationKey: Record<RiskHistoryEventKind, string> = {
    [RiskHistoryEventKind.Assessed]: "riskHistoryEventAssessed",
    [RiskHistoryEventKind.Reassessed]: "riskHistoryEventReassessed",
    [RiskHistoryEventKind.Reconfirmed]: "riskHistoryEventReconfirmed",
    [RiskHistoryEventKind.MitigationPromoted]: "riskHistoryEventMitigationPromoted",
    [RiskHistoryEventKind.ScaleMigrated]: "riskHistoryEventScaleMigrated",
    [RiskHistoryEventKind.Retired]: "riskHistoryEventRetired",
    [RiskHistoryEventKind.Reinstated]: "riskHistoryEventReinstated",
    [RiskHistoryEventKind.PlanProposed]: "riskHistoryEventPlanProposed",
    [RiskHistoryEventKind.PlanApproved]: "riskHistoryEventPlanApproved",
    [RiskHistoryEventKind.PlanCompleted]: "riskHistoryEventPlanCompleted",
    [RiskHistoryEventKind.RiskAccepted]: "riskHistoryEventRiskAccepted",
    [RiskHistoryEventKind.AreaConfigChanged]: "riskHistoryEventAreaConfigChanged",
    [RiskHistoryEventKind.RiskAvoided]: "riskHistoryEventRiskAvoided",
    [RiskHistoryEventKind.PlanStarted]: "riskHistoryEventPlanStarted",
    [RiskHistoryEventKind.PlanPlanned]: "riskHistoryEventPlanPlanned",
    [RiskHistoryEventKind.PlanDeferred]: "riskHistoryEventPlanDeferred",
    [RiskHistoryEventKind.PlanReactivated]: "riskHistoryEventPlanReactivated",
    [RiskHistoryEventKind.PlanCancelled]: "riskHistoryEventPlanCancelled",
    [RiskHistoryEventKind.MatrixRebased]: "riskHistoryEventMatrixRebased",
};

// Table/display column ids.
export enum RiskHistoryColumn {
    When = "when",
    Event = "event",
    Entity = "entity",
    Type = "type",
    Scenario = "scenario",
    Change = "change",
    By = "by",
    Why = "why",
}

export const allRiskHistoryColumns: Array<RiskHistoryColumn> = [
    RiskHistoryColumn.When,
    RiskHistoryColumn.Event,
    RiskHistoryColumn.Entity,
    RiskHistoryColumn.Type,
    RiskHistoryColumn.Scenario,
    RiskHistoryColumn.Change,
    RiskHistoryColumn.By,
    RiskHistoryColumn.Why,
];

// One ledger row. Columns a given event kind doesn't use are null. Mirrors the backend RiskHistoryEvent. Each
// risk level carries its own scale and label because a re-base leaves the original scores in the ledger, so the
// two sides of one delta can straddle a frame change.
export interface RiskHistoryEvent {
    eventTime: string;
    eventKind: RiskHistoryEventKind;
    entityType: EntityType | null;
    entityId: string | null;
    entityName: string | null;
    scenarioId: string | null;
    scenarioName: string | null;
    riskAssessmentId: string | null;
    actorName: string | null;
    consequence: number | null;
    probability: number | null;
    riskLevel: number | null;
    riskLevelLabel: string | null;
    scale: number | null;
    previousConsequence: number | null;
    previousProbability: number | null;
    previousRiskLevel: number | null;
    previousRiskLevelLabel: string | null;
    previousScale: number | null;
    consequenceJustification: string | null;
    probabilityJustification: string | null;
    reason: string | null;
    description: string | null;
    responsibleId: string | null;
    deadline: string | null;
    expectedConsequence: number | null;
    expectedProbability: number | null;
    reviewBy: string | null;
    newScale: number | null;
    oldScale: number | null;
    newAppetite: number | null;
    oldAppetite: number | null;
}

export interface RiskHistoryResult {
    items: Array<RiskHistoryEvent>;
    totalCount: number;
    totalPages: number;
}

export interface RiskHistoryRequest {
    riskAreaId: string;
    eventKinds?: Array<RiskHistoryEventKind>;
    entityTypes?: Array<EntityType>;
    // Whether events about the organisation as a whole are in scope. Undefined is no opinion, which leaves them in.
    includeOrganisationSubject?: boolean;
    entityIds?: Array<string>;
    from?: string;
    to?: string;
    searchText?: string;
    pageNumber: number;
    pageSize: number;
}
